/**
 * Curated insights for productive wait states during AI college recommendation generation
 * Aligned with College Journey UX Philosophy: "Make wait time valuable"
 */

export const RECOMMENDATION_INSIGHTS = {
  safety: [
    {
      insight: "Strategic advantage: Many overlooked safety schools offer honors colleges that rival top-tier universities in resources and outcomes.",
      category: "Strategy"
    },
    {
      insight: "Financial insight: Students in the top 25% academically at their school often receive significant merit aid - safety schools can be your most affordable option.",
      category: "Financial Aid"
    },
    {
      insight: "Hidden gem reality: Some safety schools have exceptional programs in specific fields that outrank more prestigious universities overall.",
      category: "Program Strength"
    },
    {
      insight: "Career outcome data: Many employers care more about your specific skills and internships than where you went to school - strong regional safeties often have excellent local connections.",
      category: "Career Success"
    },
    {
      insight: "Academic opportunity: Being a top student at your safety school opens doors to research positions, leadership roles, and faculty mentorship that might not be available at reach schools.",
      category: "Opportunity"
    },
    {
      insight: "Admissions reality: Your 'safety' schools still expect demonstrated interest - visit if possible, engage with admissions, and write thoughtful supplemental essays.",
      category: "Application Strategy"
    }
  ],

  target: [
    {
      insight: "Application strategy: 67% of admitted students showed demonstrated interest through campus visits or authentic engagement - target schools notice and track this.",
      category: "Strategy"
    },
    {
      insight: "Essay insight: Target school essays work best when you show how their specific programs align with your trajectory and goals, not just your general interests.",
      category: "Writing"
    },
    {
      insight: "Competitive reality: At target schools, you're competing with students with similar stats - your unique story and authentic passion become the deciding factors.",
      category: "Differentiation"
    },
    {
      insight: "Timing advantage: Many target schools have higher acceptance rates for Early Action - you get your decision early without the binding commitment of ED.",
      category: "Application Rounds"
    },
    {
      insight: "Interview opportunity: Target schools often offer optional interviews - take them. It's a chance to show personality and interest that doesn't come through on paper.",
      category: "Interviews"
    },
    {
      insight: "Major selection: Being undecided is fine for most target schools, but if you have a clear interest, applying to specific programs can show commitment and direction.",
      category: "Academic Planning"
    },
    {
      insight: "Demonstrated interest matters: Target schools track email opens, website visits, and event attendance - genuine engagement helps, generic interactions don't.",
      category: "Engagement"
    }
  ],

  reach: [
    {
      insight: "Admission reality: Reach schools admit students who fill institutional needs - not just high stats. Your unique story, background, and perspective matter more than you think.",
      category: "Holistic Review"
    },
    {
      insight: "Strategic timing: Some reach schools have 2-3x higher acceptance rates for Early Decision - but only apply ED if it's genuinely your top choice and finances work.",
      category: "Application Strategy"
    },
    {
      insight: "Essay truth: The best reach school essays show vulnerability and growth, not achievement. Admissions officers read thousands of 'I won the championship' essays - they remember authentic moments of discovery.",
      category: "Writing"
    },
    {
      insight: "Stats perspective: Once you're above their 25th percentile, additional points won't dramatically change your chances - focus on your story, not score-chasing.",
      category: "Testing"
    },
    {
      insight: "Unexpected edge: Reach schools are building communities, not just filling seats with high GPAs. What unique perspective or experience would you bring to campus discussions?",
      category: "Differentiation"
    },
    {
      insight: "Reality check: Even perfect applicants get rejected from reach schools. Have a balanced list so you're excited about all your options, not devastated by rejections.",
      category: "Expectations"
    },
    {
      insight: "Application quality over quantity: Better to submit fewer reach applications with thoughtful, school-specific supplements than to rush through many generic ones.",
      category: "Application Strategy"
    },
    {
      insight: "Financial aid reality: Most reach schools meet 100% of demonstrated need, but their definition of 'need' might differ from yours. Run net price calculators early.",
      category: "Financial Planning"
    }
  ],

  general: [
    {
      insight: "List building wisdom: Most successful applicants apply to 8-12 schools with a balanced mix of reach, target, and safety options.",
      category: "College List"
    },
    {
      insight: "Visit strategy: Virtual tours and info sessions count as demonstrated interest - you don't have to visit every school in person to show genuine interest.",
      category: "Research"
    },
    {
      insight: "Application timeline: Starting your essays in summer before senior year gives you time to write, revise, and refine - rushed essays written in October rarely represent your best work.",
      category: "Timeline"
    }
  ]
};

/**
 * Get a sequence of insights for a loading state
 * @param {string} type - 'safety', 'target', or 'reach'
 * @param {number} count - Number of insights to return
 * @returns {Array} Array of insight objects
 */
export function getLoadingInsights(type, count = 3) {
  const insights = RECOMMENDATION_INSIGHTS[type] || RECOMMENDATION_INSIGHTS.general;

  // Shuffle and return requested count
  const shuffled = [...insights].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

/**
 * Get progressive insights with timing for multi-step loading
 * Returns insights with progress percentages for smooth UX
 */
export function getProgressiveInsights(type) {
  const insights = getLoadingInsights(type, 3);

  return [
    {
      progress: 25,
      ...insights[0],
      action: "Analyzing your academic profile..."
    },
    {
      progress: 60,
      ...insights[1],
      action: "Matching with college databases..."
    },
    {
      progress: 85,
      ...insights[2],
      action: "Personalizing recommendations..."
    }
  ];
}
